export const QUESTION_TYPES = {
    TEXT: 0,
    DATE: 1,
    TIME: 2,
    DATE_TIME: 3,
    YES_NO: 4,
    TICK_BOX: 5,
    SIGNATURE: 6,
    MAP: 7,
    NUMERIC: 8,
    IMAGE: 9,
};

/**
 * Resolves a value to a usable image src.
 * Handles URLs, data URIs, and raw base64 strings
 */
export function resolveImageSrc(value) {
    if (!value) return null;
    const str = String(value);
    if (str.startsWith('http') || str.startsWith('data:')) return str;
    if (str.length > 50) {
        const cleaned = str.replace(/~+$/, '');
        const mime = cleaned.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
        return `data:${mime};base64,${cleaned}`;
    }
    return null;
}

/**
 * Colour palette for form types (status dots)
 * Each unique FormTitle maps to a distinct colour
 */
export const FORM_TYPE_COLOURS = {
    'Incident Report': '#CB2C84',
    'Accident Report': '#f44336',
    'Near Miss': '#FF9800',
    'Health & Safety': '#4CAF50',
    'Fire Alarm': '#FF5722',
    'First Aid': '#2196F3',
    'Property Damage': '#9C27B0',
    'Vehicle Incident': '#00BCD4',
    'Welfare Check': '#8BC34A',
    'Suspicious Activity': '#FFC107',
};

const FALLBACK_COLOURS = [
    '#E91E63', '#3F51B5', '#009688', '#795548',
    '#607D8B', '#CDDC39', '#FF5252', '#448AFF',
];

let dynamicIndex = 0;

/**
 * Returns a colour for a given form title.
 * Known types get a curated colour; unknown types get assigned from a fallback palette
 */
export function getFormTypeColour(formTitle) {
    if (!formTitle) return '#666';
    if (FORM_TYPE_COLOURS[formTitle]) return FORM_TYPE_COLOURS[formTitle];
    const colour = FALLBACK_COLOURS[dynamicIndex % FALLBACK_COLOURS.length];
    FORM_TYPE_COLOURS[formTitle] = colour;
    dynamicIndex++;
    return colour;
}

/**
 * Groups flat SP rows (one per question/answer per incident) by IncidentID
 * Returns an array of grouped incident objects
 */
export function groupOccurrences(flatRows) {
    if (!flatRows || flatRows.length === 0) return [];

    const map = new Map();

    for (const row of flatRows) {
        const id = row.incidentID;
        if (!map.has(id)) {
            map.set(id, {
                incidentID: id,
                dateOfIncident: row.dateOfIncident,
                siteID: row.siteID,
                siteName: row.siteName,
                clientName: row.clientName,
                department: row.department1 || '',
                department2: row.department2 || '',
                department3: row.department3 || '',
                staffID: row.staffID,
                staffName: `${row.staffFirstName || ''} ${row.staffLastName || ''}`.trim(),
                staffFirstName: row.staffFirstName,
                staffLastName: row.staffLastName,
                roleID: row.roleID,
                roleName: row.roleName,
                shiftStart: row.shiftStart,
                shiftFinish: row.shiftFinishActual,
                formID: row.formID,
                formTitle: row.formTitle,
                formDescription: row.formDescription,
                notes: row.notes,
                photoFile: row.photoFile,
                photoUrl: row.photoUrl,
                lat: row.incLat,
                lng: row.incLong,
                parentID: row.parentID,
                parentIncidentID: row.parentIncidentID,
                parentDateOfIncident: row.parentDateOfIncident,
                parentNotes: row.parentNotes,
                answers: [],
            });
        }

        if (row.questionID != null) {
            map.get(id).answers.push({
                questionID: row.questionID,
                questionDescription: row.questionDescription,
                questionType: row.questionType,
                questionTypeName: row.questionTypeName,
                answerID: row.answerID,
                answer: row.answer,
            });
        }
    }

    return Array.from(map.values());
}

/**
 * Extracts unique filter options from grouped incidents
 */
export function deriveFilterOptions(incidents) {
    const sites = new Set();
    const departments = new Set();
    const staff = new Set();
    const roles = new Set();
    const forms = new Set();

    for (const inc of incidents) {
        if (inc.siteName) sites.add(inc.siteName);
        if (inc.department) departments.add(inc.department);
        if (inc.staffName) staff.add(inc.staffName);
        if (inc.roleName) roles.add(inc.roleName);
        if (inc.formTitle) forms.add(inc.formTitle);
    }

    const toOptions = (set) => [...set].sort().map(v => ({ value: v, label: v }));

    return {
        sites: toOptions(sites),
        departments: toOptions(departments),
        staff: toOptions(staff),
        roles: toOptions(roles),
        forms: toOptions(forms),
    };
}

/**
 * Extracts time as "HH:MM" from a datetime string
 * Handles ISO format ("2026-03-06T21:00:00") and TimeSpan format ("HH:MM:SS")
 */
export function formatTime(timeVal) {
    if (!timeVal) return '';
    const str = String(timeVal);
    const tIdx = str.indexOf('T');
    if (tIdx !== -1) return str.substring(tIdx + 1, tIdx + 6);
    return str.substring(0, 5);
}

/**
 * Formats an ISO date string to DD/MM/YYYY
 */
export function formatDate(isoStr) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
}

/**
 * Aggregates incident counts by site for bar chart
 */
export function aggregateBySite(incidents) {
    const counts = {};
    for (const inc of incidents) {
        const site = inc.siteName || 'Unknown';
        counts[site] = (counts[site] || 0) + 1;
    }
    return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
}

/**
 * Aggregates incident counts over time for line/area chart
 */
export function aggregateOverTime(incidents) {
    const counts = {};
    for (const inc of incidents) {
        if (!inc.dateOfIncident) continue;
        const dateKey = new Date(inc.dateOfIncident).toISOString().split('T')[0];
        counts[dateKey] = (counts[dateKey] || 0) + 1;
    }
    return Object.entries(counts)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, count]) => ({ date, count }));
}

/**
 * Derives unique form options from all grouped incidents
 */
export function deriveFormOptions(incidents) {
    const formMap = new Map();
    for (const inc of incidents) {
        if (inc.formID && !formMap.has(inc.formID)) {
            formMap.set(inc.formID, { formID: inc.formID, formTitle: inc.formTitle });
        }
    }
    return Array.from(formMap.values());
}

/**
 * Derives questions for a given formID from all grouped incidents
 */
export function deriveQuestionsForForm(incidents, formID) {
    const questionMap = new Map();
    for (const inc of incidents) {
        if (inc.formID !== formID) continue;
        for (const a of inc.answers) {
            if (!questionMap.has(a.questionID)) {
                questionMap.set(a.questionID, {
                    questionID: a.questionID,
                    questionDescription: a.questionDescription,
                    questionType: a.questionType,
                    questionTypeName: a.questionTypeName,
                });
            }
        }
    }
    return Array.from(questionMap.values());
}

/**
 * Aggregates incident counts by form type for pie chart
 */
export function aggregateByFormType(incidents) {
    const counts = {};
    for (const inc of incidents) {
        const form = inc.formTitle || 'Unknown';
        counts[form] = (counts[form] || 0) + 1;
    }
    const total = incidents.length || 1;
    return Object.entries(counts)
        .map(([name, count]) => ({
            name,
            count,
            percentage: Math.round((count / total) * 100),
            colour: getFormTypeColour(name),
        }))
        .sort((a, b) => b.count - a.count);
}
